import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import MapComponent from '../components/MapComponent';
import PanicButton from '../components/PanicButton';
import { RatingModal } from '../components/RatingModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { deliveryAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { useCountry } from '../lib/country-context';
import { toast } from 'sonner';
import {
  Truck,
  Package,
  MapPin,
  Navigation,
  Scale,
  Clock,
  DollarSign,
  ArrowRight,
  X,
  CheckCircle2,
  Bike,
  Box
} from 'lucide-react';

const deliveryTypes = [
  { id: 'errand', name: 'Mandado', icon: Bike, description: 'Entregas pequeñas y rápidas', basePrice: 3 },
  { id: 'small_freight', name: 'Flete Pequeño', icon: Package, description: 'Hasta 20kg', basePrice: 8 },
  { id: 'medium_freight', name: 'Flete Mediano', icon: Box, description: 'Hasta 100kg', basePrice: 15 },
  { id: 'large_freight', name: 'Flete Grande', icon: Truck, description: 'Hasta 500kg', basePrice: 30 },
];

const DeliveryStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-600', label: 'Buscando repartidor' },
    accepted: { color: 'bg-blue-100 text-blue-600', label: 'Repartidor asignado' },
    picked_up: { color: 'bg-purple-100 text-purple-600', label: 'Recogido' },
    in_transit: { color: 'bg-green-100 text-green-600', label: 'En tránsito' },
    delivered: { color: 'bg-teal-100 text-teal-600', label: 'Entregado' },
    cancelled: { color: 'bg-red-100 text-red-600', label: 'Cancelado' },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

export default function DeliveryPage() {
  const { refreshUser } = useAuth();
  const { country, formatCurrency, convertFromUSD } = useCountry();
  const [step, setStep] = useState('form');
  const [pickupLocation, setPickupLocation] = useState(null);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [selectingLocation, setSelectingLocation] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [completedDelivery, setCompletedDelivery] = useState(null);

  const [formData, setFormData] = useState({
    pickupAddress: '',
    deliveryAddress: '',
    deliveryType: 'errand',
    packageDescription: '',
    packageWeight: 1,
  });

  useEffect(() => {
    loadDeliveries();
  }, []);

  useEffect(() => {
    if (pickupLocation && deliveryLocation) {
      calculatePrice();
    }
  }, [pickupLocation, deliveryLocation, formData.deliveryType, formData.packageWeight]);

  const calculateDistance = (loc1, loc2) => {
    const R = 6371;
    const dLat = (loc2.lat - loc1.lat) * Math.PI / 180;
    const dLon = (loc2.lng - loc1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(loc1.lat * Math.PI / 180) * Math.cos(loc2.lat * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const calculatePrice = () => {
    if (!pickupLocation || !deliveryLocation) return;
    
    const distance = calculateDistance(pickupLocation, deliveryLocation);
    const multiplier = { errand: 1, small_freight: 1.5, medium_freight: 2.5, large_freight: 4 };
    const baseFare = 3;
    const perKm = 0.6;
    const weightFactor = 1 + (formData.packageWeight * 0.1);
    
    const price = (baseFare + (distance * perKm)) * (multiplier[formData.deliveryType] || 1) * weightFactor;
    setEstimatedPrice(price.toFixed(2));
  };

  const loadDeliveries = async () => {
    try {
      const response = await deliveryAPI.getAll();
      setDeliveries(response.data);
      const active = response.data.find(d => ['pending', 'accepted', 'picked_up', 'in_transit'].includes(d.status));
      if (active) {
        setActiveDelivery(active);
        setStep('tracking');
      }
    } catch (error) {
      console.error('Error loading deliveries:', error);
    }
  };

  const handleMapClick = (location) => {
    if (selectingLocation === 'pickup') {
      setPickupLocation(location);
      setFormData(prev => ({ ...prev, pickupAddress: `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` }));
      setSelectingLocation(null);
    } else if (selectingLocation === 'delivery') {
      setDeliveryLocation(location);
      setFormData(prev => ({ ...prev, deliveryAddress: `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` }));
      setSelectingLocation(null);
    }
  };

  const handleSubmit = async () => {
    if (!pickupLocation || !deliveryLocation) {
      toast.error('Selecciona las ubicaciones');
      return;
    }
    if (!formData.packageDescription) {
      toast.error('Describe el paquete');
      return;
    }

    setLoading(true);
    try {
      const response = await deliveryAPI.create({
        pickup_location: { ...pickupLocation, address: formData.pickupAddress },
        delivery_location: { ...deliveryLocation, address: formData.deliveryAddress },
        delivery_type: formData.deliveryType,
        package_description: formData.packageDescription,
        package_weight_kg: parseFloat(formData.packageWeight),
        payment_method: 'wallet',
      });
      
      setActiveDelivery(response.data);
      setStep('tracking');
      toast.success('¡Envío solicitado! Buscando repartidor...');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar envío');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelivery = async () => {
    if (!activeDelivery) return;
    
    try {
      await deliveryAPI.updateStatus(activeDelivery.id, 'cancelled');
      setActiveDelivery(null);
      setStep('form');
      loadDeliveries();
      toast.info('Envío cancelado');
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  const handleCompleteDelivery = async () => {
    if (!activeDelivery) return;
    
    try {
      await deliveryAPI.updateStatus(activeDelivery.id, 'delivered');
      setCompletedDelivery(activeDelivery);
      setShowRatingModal(true);
      setActiveDelivery(null);
      setStep('form');
      loadDeliveries();
      toast.success('¡Entrega completada!');
    } catch (error) {
      toast.error('Error al completar');
    }
  };

  const resetForm = () => {
    setActiveDelivery(null);
    setStep('form');
    setPickupLocation(null);
    setDeliveryLocation(null);
    setFormData({
      pickupAddress: '',
      deliveryAddress: '',
      deliveryType: 'errand',
      packageDescription: '',
      packageWeight: 1,
    });
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)] lg:h-screen flex flex-col lg:flex-row">
        {/* Map Section */}
        <div className="flex-1 relative">
          <MapComponent
            pickupLocation={pickupLocation}
            destinationLocation={deliveryLocation}
            onMapClick={handleMapClick}
            showRoute={pickupLocation && deliveryLocation}
            className="h-64 lg:h-full"
          />
          
          {selectingLocation && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white shadow-lg px-4 py-2 rounded-xl z-20">
              <p className="text-slate-800 text-sm font-medium">
                Toca el mapa para seleccionar {selectingLocation === 'pickup' ? 'recogida' : 'entrega'}
              </p>
            </div>
          )}
        </div>

        {/* Panic Button */}
        {activeDelivery && ['accepted', 'picked_up', 'in_transit'].includes(activeDelivery.status) && (
          <PanicButton
            serviceType="delivery"
            serviceId={activeDelivery.id}
            userLocation={pickupLocation || { lat: 19.4326, lng: -99.1332 }}
          />
        )}

        {/* Control Panel - Light Mode */}
        <div className="w-full lg:w-96 bg-white border-t lg:border-t-0 lg:border-l border-slate-200 overflow-y-auto">
          {step === 'form' && (
            <div className="p-6 space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 font-['Outfit']">Envíos y Mandados</h2>
                <p className="text-slate-500 text-sm mt-1">¿Qué necesitas enviar?</p>
              </div>

              {/* Delivery Type Selection */}
              <div className="space-y-3">
                <Label className="text-slate-700">Tipo de envío</Label>
                <div className="grid grid-cols-2 gap-3">
                  {deliveryTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setFormData(prev => ({ ...prev, deliveryType: type.id }))}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        formData.deliveryType === type.id
                          ? 'border-[#00BFA6] bg-[#00BFA6]/10'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`delivery-type-${type.id}`}
                    >
                      <type.icon className={`w-6 h-6 mb-2 ${
                        formData.deliveryType === type.id ? 'text-[#00BFA6]' : 'text-slate-400'
                      }`} />
                      <p className={`text-sm font-medium ${
                        formData.deliveryType === type.id ? 'text-[#00BFA6]' : 'text-slate-700'
                      }`}>{type.name}</p>
                      <p className="text-xs text-slate-500">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Inputs */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Recoger en</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#00BFA6]" />
                      <Input
                        placeholder="Selecciona en el mapa"
                        value={formData.pickupAddress}
                        onChange={(e) => setFormData(prev => ({ ...prev, pickupAddress: e.target.value }))}
                        className="pl-11 h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                        data-testid="pickup-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      className={`h-12 px-4 rounded-xl border-slate-200 ${
                        selectingLocation === 'pickup' ? 'bg-[#00BFA6]/10 border-[#00BFA6] text-[#00BFA6]' : 'text-slate-500'
                      }`}
                      onClick={() => setSelectingLocation(selectingLocation === 'pickup' ? null : 'pickup')}
                    >
                      <MapPin className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">Entregar en</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FFD166]" />
                      <Input
                        placeholder="Selecciona en el mapa"
                        value={formData.deliveryAddress}
                        onChange={(e) => setFormData(prev => ({ ...prev, deliveryAddress: e.target.value }))}
                        className="pl-11 h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                        data-testid="delivery-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      className={`h-12 px-4 rounded-xl border-slate-200 ${
                        selectingLocation === 'delivery' ? 'bg-[#FFD166]/10 border-[#FFD166] text-[#FFD166]' : 'text-slate-500'
                      }`}
                      onClick={() => setSelectingLocation(selectingLocation === 'delivery' ? null : 'delivery')}
                    >
                      <Navigation className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Package Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Descripción del paquete</Label>
                  <Textarea
                    placeholder="Describe qué vas a enviar..."
                    value={formData.packageDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, packageDescription: e.target.value }))}
                    className="bg-slate-50 border-slate-200 text-slate-800 rounded-xl resize-none h-20"
                    data-testid="package-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">Peso aproximado (kg)</Label>
                  <div className="flex items-center gap-3">
                    <Scale className="w-5 h-5 text-slate-400" />
                    <Input
                      type="number"
                      min="0.1"
                      max="500"
                      step="0.5"
                      value={formData.packageWeight}
                      onChange={(e) => setFormData(prev => ({ ...prev, packageWeight: e.target.value }))}
                      className="h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                      data-testid="package-weight"
                    />
                    <span className="text-slate-500">kg</span>
                  </div>
                </div>
              </div>

              {/* Price Estimate */}
              {pickupLocation && deliveryLocation && (
                <div className="bg-slate-50 p-4 rounded-xl animate-slide-up">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#00BFA6]" />
                      <span className="text-slate-600">Precio estimado</span>
                    </div>
                    <span className="text-2xl font-bold text-slate-800">
                      {formatCurrency(convertFromUSD(estimatedPrice))}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-slate-500 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>Recogida en ~20-30 min</span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={loading || !pickupLocation || !deliveryLocation || !formData.packageDescription}
                className="w-full h-14 btn-primary text-lg"
                data-testid="request-delivery-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Solicitar Envío
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Tracking View */}
          {step === 'tracking' && activeDelivery && (
            <div className="p-6 space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-800 font-['Outfit']">Tu Envío</h2>
                <DeliveryStatusBadge status={activeDelivery.status} />
              </div>

              {/* Driver Info */}
              <Card className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#00BFA6]/10 flex items-center justify-center">
                      <Truck className="w-7 h-7 text-[#00BFA6]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-800 font-semibold">{activeDelivery.driver_name || 'Buscando repartidor...'}</p>
                      <p className="text-slate-500 text-sm">{deliveryTypes.find(t => t.id === activeDelivery.delivery_type)?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-800">
                        {formatCurrency(convertFromUSD(activeDelivery.price || 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Package Info */}
              <div className="bg-slate-50 p-4 rounded-xl">
                <p className="text-slate-500 text-xs mb-1">Paquete</p>
                <p className="text-slate-800">{activeDelivery.package_description}</p>
                <p className="text-slate-500 text-sm mt-1">{activeDelivery.package_weight_kg} kg</p>
              </div>

              {/* Route Info */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#00BFA6] mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Recoger en</p>
                    <p className="text-slate-800">{activeDelivery.pickup_location?.address || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-[#FFD166] mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Entregar en</p>
                    <p className="text-slate-800">{activeDelivery.delivery_location?.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {activeDelivery.status === 'in_transit' && (
                  <Button
                    onClick={handleCompleteDelivery}
                    className="w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                    data-testid="complete-delivery-btn"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Marcar como Entregado (Demo)
                  </Button>
                )}
                
                {activeDelivery.status === 'pending' && (
                  <Button
                    onClick={handleCancelDelivery}
                    variant="outline"
                    className="w-full h-12 border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                    data-testid="cancel-delivery-btn"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Cancelar Envío
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Delivery History */}
          <div className="p-6 border-t border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 font-['Outfit']">Historial</h3>
            {deliveries.filter(d => d.status === 'delivered' || d.status === 'cancelled').length > 0 ? (
              <div className="space-y-3">
                {deliveries.filter(d => d.status === 'delivered' || d.status === 'cancelled').slice(0, 5).map((delivery) => (
                  <div key={delivery.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-slate-800 text-sm font-medium">{delivery.package_description?.slice(0, 20) || 'N/A'}...</p>
                        <p className="text-slate-500 text-xs">{new Date(delivery.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <DeliveryStatusBadge status={delivery.status} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No hay envíos anteriores</p>
            )}
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        serviceType="delivery"
        serviceId={completedDelivery?.id}
        ratedUserId={completedDelivery?.driver_id}
        ratedUserName={completedDelivery?.driver_name}
        canSkip={true}
        onSkip={() => setShowRatingModal(false)}
        onSuccess={() => {
          setShowRatingModal(false);
          setCompletedDelivery(null);
        }}
      />
    </Layout>
  );
}
